import { Button, message } from 'antd';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import HeaderWithColumnSelectionExport from '~/components/organisms/Shared/HeaderWithColumnSelectionExport';
import { SaleStatementTypeEnum } from '~/domain/enum/salesStatement/saleStatementType.enum';
import { ISalesStatementExportColumn } from '~/domain/model/SalesStatement/SalesStatement';
import { buildExportCheckedKeys } from '~/helpers/util/buildExportCheckedKeys';
import { postSalesStatementExport } from '~/services/producer/SalesStatement/SalesStatementService';
import useSaleStatementByTypeStore from '~/zustand/salesStatement/useSalesStatementByTypeStore';
import { IExportColumn } from '../../Shared/GenericExportModal/GenericExportModal';

export const title = 'Extrato de Vendas Klok';
export const subtitle =
  'Acompanhe os números de vendas Klok. Analise, filtre e extraia relatórios.';

const EXPORT_COLUMNS: Omit<IExportColumn, 'checked'>[] = [
  { key: 'salesChannel', label: 'Origem' },
  { key: 'transactionDate', label: 'Data do Pedido' },
  { key: 'billingDate', label: 'Data de Entrega' },
  { key: 'commercialDate', label: 'Data do Leilão' },
  { key: 'orderNumber', label: 'Nº da Transação' },
  { key: 'gfpNumber', label: 'Nº da GFP' },
  { key: 'siteCode', label: 'Chave' },
  { key: 'customerDescription', label: 'Cliente' },
  { key: 'productLongDescription', label: 'Produto' },
  { key: 'productCode', label: 'Código do Produto' },
  { key: 'qualityDescription', label: 'QLD.' },
  { key: 'packingCode', label: 'Emb.' },
  { key: 'packingDescription', label: 'Descrição da Embalagem' },
  { key: 'quantity', label: 'QE x QpE' },
  { key: 'totalQuantity', label: 'Quant. Total' },
  { key: 'unitValue', label: 'Valor Unitário' },
  { key: 'packingValue', label: 'Valor da Embalagem' },
  { key: 'freightValue', label: 'Valor do Frete' },
  { key: 'totalValue', label: 'Valor Total' },
];

const EXPORT_COLUMN_KEYS = EXPORT_COLUMNS.map(column => column.key);

/**
 * Colunas da exportação sem correspondente na tabela (productCode, packingDescription)
 * ficam sempre marcadas — o usuário não tem como ocultá-las em Filtrar Colunas.
 */
const TABLE_KEY_BY_EXPORT_KEY: Record<string, string> = {
  salesChannel: 'salesChannel',
  transactionDate: 'transactionDate',
  billingDate: 'billingDate',
  commercialDate: 'commercialDate',
  orderNumber: 'orderNumber',
  gfpNumber: 'gfpNumber',
  siteCode: 'siteCode',
  customerDescription: 'customerDescription',
  productLongDescription: 'productDescription',
  qualityDescription: 'qualityDescription',
  packingCode: 'packingCode',
  quantity: 'quantity',
  totalQuantity: 'totalQuantity',
  unitValue: 'unitValue',
  packingValue: 'packingValue',
  freightValue: 'freightValue',
  totalValue: 'totalValue',
};

export default function SalesStatementKlokHeader() {
  const { formInstance, isDisabledButton, getExportParams, visibleColumnKeys } =
    useSaleStatementByTypeStore(
      useShallow(state => {
        return {
          formInstance: state.klokFormFilterInstance,
          isDisabledButton: isEmpty(state.klokTable.data),
          getExportParams: state.getExportParams,
          visibleColumnKeys: state.klokVisibleColumnKeys,
        };
      }),
    );

  const handleOpenModal = useCallback(async () => {
    try {
      await formInstance?.validateFields?.();
    } catch (errorInfo) {
      message.warning(
        'Erro ao exportar os Extratos de Vendas Klok. Por favor, tente novamente mais tarde',
      );
      throw errorInfo;
    }
  }, [formInstance]);

  const handleGetExcelExport = async (selectedColumns: string[]) => {
    await formInstance?.validateFields?.();
    const fields = await formInstance?.getFieldsValue?.();
    if (!fields)
      return Promise.reject(
        new Error(
          'Erro ao exportar os Extratos de Vendas Klok. Por favor, tente novamente mais tarde',
        ),
      );

    const exportColumnsPayload = selectedColumns.reduce(
      (acc, column) => ({ ...acc, [column]: true }),
      {} as ISalesStatementExportColumn,
    );

    const params = getExportParams(SaleStatementTypeEnum.Klok, exportColumnsPayload);
    return postSalesStatementExport(params);
  };

  const handleSummaryTable = () => {
    const summaryElement = document.getElementById('summary-klok');
    if (summaryElement) {
      summaryElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const additionalAction = (
    <Button type="ghost" size="large" onClick={handleSummaryTable} disabled={isDisabledButton}>
      Resumo Klok
    </Button>
  );

  const exportColumns: IExportColumn[] = useMemo(() => {
    const checkedKeys = buildExportCheckedKeys(
      EXPORT_COLUMN_KEYS,
      TABLE_KEY_BY_EXPORT_KEY,
      visibleColumnKeys,
    );

    return EXPORT_COLUMNS.map(column => ({ ...column, checked: checkedKeys.has(column.key) }));
  }, [visibleColumnKeys]);

  return (
    <HeaderWithColumnSelectionExport
      title={title}
      subtitle={subtitle}
      isDisabledButton={isDisabledButton}
      fileName="relatório-extrato-vendas-klok.xlsx"
      onClickOpenModal={handleOpenModal}
      getExcelExport={handleGetExcelExport}
      exportColumns={exportColumns}
      additionalAction={additionalAction}
    />
  );
}
